import {
    AppPlatform,
    DatadogCustomMetric,
    DatadogMetricTagFilter,
    EventAndParam,
    Integration,
    IntegrationEntity,
    IntegrationInstallationId,
    MondayBoardColumnPair,
    WorkflowType,
} from './../types';
import { AuthUpdatable, IntegrationDeletable, IntegrationRefreshable, IntegrationUpdatable } from './updatable';
import { AppEntity, IntegrationType, OrganizationEntity } from '../types';
import { UpdateAppSettingsResponse } from './appSettings';
import { BaseAuthedApiRequest } from '../api';

export const CREATE_INTEGRATION_REQUEST_SUCCESS = 'CREATE_INTEGRATION_REQUEST_SUCCESS';
export const UPDATE_INTEGRATION_REQUEST_SUCCESS = 'UPDATE_INTEGRATION_REQUEST_SUCCESS';
export const UPDATE_INTEGRATIONS_REQUEST_SUCCESS = 'UPDATE_INTEGRATIONS_REQUEST_SUCCESS';
export const DELETE_INTEGRATION_REQUEST_SUCCESS = 'DELETE_INTEGRATION_REQUEST_SUCCESS';
export const REFRESH_INTEGRATION_REQUEST_SUCCESS = 'REFRESH_INTEGRATION_REQUEST_SUCCESS';
export const TRY_UPDATE_INTEGRATION_ALIAS = 'TRY_UPDATE_INTEGRATION_ALIAS';

export interface SlackIntegrationRequestDataType {
    oAuthAuthorizationCode: string;
    redirectUri: string;
}

export interface GitHubIntegrationRequestDataType {
    installationId: string;
    setupAction: string;
}

export interface GitLabIntegrationRequestDataType {
    oAuthAuthorizationCode: string;
    redirectUri: string;
}

export interface BitriseIntegrationRequestDataType {
    accessToken: string;
}

export interface BuildkiteIntegrationRequestDataType {
    accessToken: string;
}

export interface AppleIntegrationRequestDataType {
    keyId: string;
    issuerId: string;
    s3Key: string;
    shouldAddKeyToBuildSecrets?: boolean;
}

export interface JenkinsIntegrationRequestDataType {
    url: string;
    username: string;
    accessToken: string;
}

export interface CircleCIIntegrationRequestDataType {
    accessToken: string;
}

export interface GoogleIntegrationRequestDataType {
    packageName: string;
    developerId: string;
    s3Key: string;
    shouldAddKeyToBuildSecrets?: boolean;
    propertyId: string;
}

export interface HuaweiIntegrationRequestDataType {
    clientId: string;
    clientSecret: string;
}

export interface SamsungIntegrationRequestDataType {
    serviceAccountId: string;
}

export interface AmazonIntegrationRequestDataType {
    clientId: string;
    clientSecret: string;
}

export interface AmplitudeIntegrationRequestDataType {
    apiKey: string;
    apiSecret: string;
    shouldUseEUServer: boolean;
}

export interface OptimizelyIntegrationRequestDataType {
    accessToken: string;
}

export interface LaunchDarklyIntegrationRequestDataType {
    accessToken: string;
}

export interface StatsigIntegrationRequestDataType {
    accessToken: string; // API key
}

export interface StatsigIntegrationUpdateRequestData {
    environmentId: string;
    tags: string[];
    tagsMatchAny: boolean;
    versionAttributeName?: string | null;
    featureFlagPlatformFilter?: string | null;
}

export interface AmplitudeExperimentIntegrationRequestDataType {
    accessToken: string; // Management API key
    shouldUseEUServer: boolean;
}

export interface AmplitudeExperimentIntegrationUpdateRequestData {
    projectId: string;
    deploymentIds: string[];
}

export interface UnleashIntegrationRequestDataType {
    url: string; // Unleash instance root
    accessToken: string; // Admin, service account or personal access token
}

export interface UnleashIntegrationUpdateRequestData {
    projectId: string;
    environmentId: string;
    versionAttributeName?: string | null;
    tags: string[];
    tagsMatchAny: boolean;
}

export interface CrowdinIntegrationRequestDataType {
    accessToken: string;
    subdomain?: string;
}

export interface LokaliseIntegrationRequestDataType {
    accessToken: string;
}

export interface PagerDutyIntegrationRequestDataType {
    accessToken: string;
}

export interface OpsgenieIntegrationRequestDataType {
    accessToken: string;
    shouldUseEUServer: boolean;
}

export interface RootlyIntegrationRequestDataType {
    accessToken: string;
}

export interface IncidentIoIntegrationRequestDataType {
    accessToken: string;
}

export enum DatadogDatacenter {
    US1 = 'us1',
    US3 = 'us3',
    US5 = 'us5',
    EU = 'eu',
    US1Fed = 'fed',
    AP1 = 'ap1',
    AP2 = 'ap2',
}

export interface DatadogIntegrationRequestDataType {
    apiKey: string;
    applicationKey: string;
    datadogDatacenter: DatadogDatacenter;
}

export interface DynatraceIntegrationRequestDataType {
    accessToken: string;
    environmentId: string;
}

export interface LuciqIntegrationRequestDataType {
    accessToken: string;
    email: string;
}

export interface MixpanelIntegrationRequestDataType {
    serviceAccountUser: string;
    serviceAccountSecret: string;
    shouldUseEUServer: boolean;
}

export enum BugsnagDomain {
    Bugsnag = 'bugsnag.com',
    SmartBear = 'bugsnag.smartbear.com',
}

export interface BugsnagIntegrationRequestDataType {
    accessToken: string;
    bugsnagDomain: BugsnagDomain;
}

export interface GoogleAnalyticsIntegrationRequestDataType {
    propertyId: string;
}

export interface InstallIntegrationFromProviderRequestDataType {
    providerId: string;
}

export interface GitLabBaseIntegrationRequestDataType {}

export interface EmbraceIntegrationRequestDataType {
    apiKey: string;
}

export interface CustomAnalyticsIntegrationRequestDataType {
    displayableName: string;
}

export interface CustomCIIntegrationRequestDataType {
    displayableName: string;
}

export interface ExpoEASIntegrationRequestDataType {
    accessToken: string;
    expoAccountId: string;
}

export interface RunwayBetaBaseIntegrationRequestDataType {}

export type CreateIntegrationRequestDataType =
    | SlackIntegrationRequestDataType
    | GitHubIntegrationRequestDataType
    | GitLabIntegrationRequestDataType
    | BitriseIntegrationRequestDataType
    | BuildkiteIntegrationRequestDataType
    | AppleIntegrationRequestDataType
    | AmazonIntegrationRequestDataType
    | JenkinsIntegrationRequestDataType
    | CircleCIIntegrationRequestDataType
    | GoogleIntegrationRequestDataType
    | HuaweiIntegrationRequestDataType
    | GitLabBaseIntegrationRequestDataType
    | InstallIntegrationFromProviderRequestDataType
    | AmplitudeIntegrationRequestDataType
    | OptimizelyIntegrationRequestDataType
    | LaunchDarklyIntegrationRequestDataType
    | StatsigIntegrationRequestDataType
    | AmplitudeExperimentIntegrationRequestDataType
    | UnleashIntegrationRequestDataType
    | DatadogIntegrationRequestDataType
    | DynatraceIntegrationRequestDataType
    | LuciqIntegrationRequestDataType
    | MixpanelIntegrationRequestDataType
    | BugsnagIntegrationRequestDataType
    | EmbraceIntegrationRequestDataType
    | GoogleAnalyticsIntegrationRequestDataType
    | RunwayBetaBaseIntegrationRequestDataType
    | CustomAnalyticsIntegrationRequestDataType
    | CustomCIIntegrationRequestDataType
    | ExpoEASIntegrationRequestDataType;

// Update integrations

export interface BaseUpdateCIIntegrationRequestData {
    workflowId: string;
    devWorkflowId?: string;
    prodWorkflowId?: string;
    allowMatchingMultipleAppStoreBuilds?: boolean;
    isBuildVariablesFilteringStrict?: boolean;
    buildVariables?: Record<WorkflowType, Record<string, string>>;
}

export interface CustomCIIntegrationUpdateRequestData {
    allowMatchingMultipleAppStoreBuilds?: boolean | null;
    // Optional outbound build trigger. Header values are secret; an empty value keeps the existing one.
    customCITriggerUrl?: string;
    customCITriggerBodyTemplate?: string;
    customCITriggerHeaders?: Record<string, string>;
}

export interface GitHubVCSIntegrationUpdateRequestData {
    repoName: string;
}

export interface GitHubCIIntegrationUpdateRequestData extends BaseUpdateCIIntegrationRequestData {
    repoName: string;
}

export interface GitHubITIntegrationUpdateRequestData {
    repoName: string;
    additionalDoneStatuses?: string[];
}

export interface GitLabVCSIntegrationUpdateRequestData {
    projectId: string;
}

export interface GitLabCIIntegrationUpdateRequestData extends BaseUpdateCIIntegrationRequestData {
    projectId: string;
}

export interface BitriseIntegrationUpdateRequestData extends BaseUpdateCIIntegrationRequestData {
    appSlug: string;
    prodAppSlug?: string;
    devAppSlug?: string;
}

export interface XcodeCloudIntegrationUpdateRequestData extends BaseUpdateCIIntegrationRequestData {
    appSlug: string;
    prodAppSlug?: string;
    devAppSlug?: string;
}

export interface BuildkiteIntegrationUpdateRequestData extends BaseUpdateCIIntegrationRequestData {
    orgId: string;
}

export interface AzureCIIntegrationUpdateRequestData extends BaseUpdateCIIntegrationRequestData {
    projectId: string;
}

export interface AzureVCSIntegrationUpdateRequestData {
    projectId: string;
    repoName: string;
}

export interface AzureITIntegrationUpdateRequestData {
    projectId: string;
    projects: string[]; // for workarea ids
    additionalDoneStatuses?: string[];
}

export interface JenkinsIntegrationUpdateRequestData extends BaseUpdateCIIntegrationRequestData {
    subWorkflowId?: string;
    prodSubWorkflowId?: string;
    devSubWorkflowId?: string;
}

export interface CircleCIUpdateRequestData extends BaseUpdateCIIntegrationRequestData {
    collaborationId: string;
    repoName: string;
}

export interface JiraIntegrationUpdateRequestData {
    projects: string[];
    additionalDoneStatuses?: string[];
    customFields?: string[];
    autoSelectAllProjects: boolean;
}

export interface AsanaIntegrationUpdateRequestData {
    projects: string[];
    additionalDoneStatuses?: string[];
}

export interface PivotalIntegrationUpdateRequestData {
    projectIds: number[];
}

export interface BugsnagIntegrationUpdateRequestData {
    orgId: string;
    projectId: string;
}

export interface LinearIntegrationUpdateRequestData {
    teams: string[];
    additionalDoneStatuses?: string[];
}

export interface MondayIntegrationUpdateRequestData {
    boardIds: string[];
    faColumns: MondayBoardColumnPair[];
    doneColumns: MondayBoardColumnPair[];
    additionalDoneStatuses: string[];
}

export interface CommsIntegrationUpdateRequestData {
    primaryChannelIds: string[];
}

export interface AppleIntegrationUpdateRequestData {
    bundleId: string;
    appleAppId: number;
    betaGroupIds: string[];
}

export interface GoogleIntegrationUpdateRequestData {
    packageName: string;
    developerId: string;
    consoleAppId?: string;
}

export interface HuaweiIntegrationUpdateRequestData {
    packageName: string;
    developerId: string;
}

export interface SamsungIntegrationUpdateRequestData {
    contentId: string;
}

export interface ExpoEASIntegrationUpdateRequestData {
    projectId: string;
    projectSlug: string;
    owner: string;
    channel: string;
}

export interface AmplitudeIntegrationUpdateRequestData {
    userSelectedEvents: string[];
}

export interface DatadogAnalyticsIntegrationUpdateRequestData {
    rumApplicationId: string;
    selectedEventsAndParams: EventAndParam[];
    datadogCustomMetrics: DatadogCustomMetric[];
    datadogMetricTagFilters: DatadogMetricTagFilter[];
    datadogSingleVersionPerFetch: boolean;
    datadogSkipCustomMetricsFilterByService: boolean;
    datadogProdServiceValue: string;
    datadogBetaServiceValue: null;
    shouldFilterByBundleId: boolean;
}

export interface DatadogStabilityMonitoringIntegrationUpdateRequestData {
    rumApplicationId: string;
    prodEnvironmentIdentifier: string;
    betaEnvironmentIdentifier?: string;
    shouldFilterByBundleId: boolean;
    datadogProdServiceValue: string;
    datadogBetaServiceValue?: string | null;
}

export interface DynatraceIntegrationUpdateRequestData {
    rumApplicationId: string;
}

export interface LuciqStabilityMonitoringIntegrationUpdateRequestData {
    luciqAppSlug: string;
    luciqAppMode: string;
    luciqAppToken: string;
}

export interface PagerDutyIntegrationUpdateRequestData {
    scheduleId: string;
}

export interface OpsgenieIntegrationUpdateRequestData {
    scheduleId: string;
}

export interface JiraServiceManagementIntegrationUpdateRequestData {
    scheduleId: string;
}

export interface MixpanelAnalyticsIntegrationUpdateRequestData {
    mixpanelProjectId: number;
    userSelectedEvents: string[];
}

export interface GoogleAnalyticsIntegrationUpdateRequestData {
    userSelectedEvents: string[];
    dataStreamId: string;
}

export interface FirebaseBetaIntegrationUpdateRequestData {
    projectId: string;
    appId: string;
    betaGroupIds: string[];
}

export interface FirebaseFeatureFlaggingIntegrationUpdateRequestData {
    projectId: string;
}

export interface RunwayBuildDistroBetaIntegrationUpdateRequestData {
    bucketId: string;
}

export interface EmbraceStabilityMonitoringIntegrationUpdateRequestData {
    prodAppSlug?: string;
    devAppSlug?: string;
}

export interface NewRelicStabilityMonitoringIntegrationUpdateRequestData {
    prodEnvironmentIdentifier: string; // New Relic prod app guid
    betaEnvironmentIdentifier?: string; // New Relic beta app guid
}

export interface NewRelicAnalyticsIntegrationUpdateRequestData {
    appId: string;
    userSelectedEvents: string[];
}

export interface GoogleCalendarIntegrationUpdateRequestData {
    calendarId: string;
    serviceAccountEmail: string;
    appDisplayName?: string;
}

export interface GooglePersonalCalendarIntegrationUpdateRequestData {
    calendarId: string;
    appDisplayName?: string;
}

export type UpdateIntegrationRequestDataType =
    | GitHubVCSIntegrationUpdateRequestData
    | GitHubCIIntegrationUpdateRequestData
    | GitHubITIntegrationUpdateRequestData
    | GitLabVCSIntegrationUpdateRequestData
    | GitLabCIIntegrationUpdateRequestData
    | BitriseIntegrationUpdateRequestData
    | XcodeCloudIntegrationUpdateRequestData
    | BuildkiteIntegrationUpdateRequestData
    | JenkinsIntegrationUpdateRequestData
    | CircleCIUpdateRequestData
    | JiraIntegrationUpdateRequestData
    | AsanaIntegrationUpdateRequestData
    | PivotalIntegrationUpdateRequestData
    | LinearIntegrationUpdateRequestData
    | CommsIntegrationUpdateRequestData
    | AppleIntegrationUpdateRequestData
    | GoogleIntegrationUpdateRequestData
    | HuaweiIntegrationUpdateRequestData
    | SamsungIntegrationUpdateRequestData
    | BugsnagIntegrationUpdateRequestData
    | AmplitudeIntegrationUpdateRequestData
    | AzureCIIntegrationUpdateRequestData
    | AzureITIntegrationUpdateRequestData
    | DatadogAnalyticsIntegrationUpdateRequestData
    | MixpanelAnalyticsIntegrationUpdateRequestData
    | MondayIntegrationUpdateRequestData
    | FirebaseBetaIntegrationUpdateRequestData
    | FirebaseFeatureFlaggingIntegrationUpdateRequestData
    | GoogleAnalyticsIntegrationUpdateRequestData
    | RunwayBuildDistroBetaIntegrationUpdateRequestData
    | EmbraceStabilityMonitoringIntegrationUpdateRequestData
    | AzureVCSIntegrationUpdateRequestData
    | DatadogStabilityMonitoringIntegrationUpdateRequestData
    | DynatraceIntegrationUpdateRequestData
    | LuciqStabilityMonitoringIntegrationUpdateRequestData
    | PagerDutyIntegrationUpdateRequestData
    | NewRelicStabilityMonitoringIntegrationUpdateRequestData
    | NewRelicAnalyticsIntegrationUpdateRequestData
    | OpsgenieIntegrationUpdateRequestData
    | JiraServiceManagementIntegrationUpdateRequestData
    | GoogleCalendarIntegrationUpdateRequestData
    | GooglePersonalCalendarIntegrationUpdateRequestData
    | StatsigIntegrationUpdateRequestData
    | AmplitudeExperimentIntegrationUpdateRequestData
    | UnleashIntegrationUpdateRequestData
    | ExpoEASIntegrationUpdateRequestData
    | CustomCIIntegrationUpdateRequestData;

export interface IntegrationRequest extends BaseAuthedApiRequest {
    integrationId: IntegrationInstallationId;
    appId: string;
}

export interface CreateIntegrationRequest extends BaseAuthedApiRequest {
    integrationId: IntegrationType;
    appId: string;
    data: CreateIntegrationRequestDataType;
}

export interface UpdateIntegrationRequest extends IntegrationRequest {
    data: UpdateIntegrationRequestDataType | Partial<UpdateIntegrationRequestDataType>;
    isPartialUpdate?: boolean;
    platform?: AppPlatform;
}

export interface UpdateIntegrationAliasRequest extends IntegrationRequest {
    alias: string;
}

// Refresh
export interface RefreshIntegrationRequest extends IntegrationRequest {}

export interface TryRefreshIntegrationAction {
    type: IntegrationRefreshable;
    payload: RefreshIntegrationRequest;
}

export interface RefreshIntegrationSuccessAction {
    type: typeof REFRESH_INTEGRATION_REQUEST_SUCCESS;
    payload: CreateDeleteIntegrationResponse;
}

export const tryRefreshIntegrationRequest: (
    type: IntegrationRefreshable,
    payload: RefreshIntegrationRequest,
) => TryRefreshIntegrationAction = (type, payload) => {
    return {
        type,
        payload,
    };
};

export const refreshIntegrationSuccess: (
    response: CreateDeleteIntegrationResponseType,
) => RefreshIntegrationSuccessAction = (response) => {
    return {
        type: REFRESH_INTEGRATION_REQUEST_SUCCESS,
        payload: response,
    };
};

export interface CreateDeleteIntegrationResponse {
    appEntity: AppEntity;
    orgEntity: OrganizationEntity;
    integrationEntity: IntegrationEntity;
}

export type CreateIntegrationRequestType = CreateIntegrationRequest;

export type CreateDeleteIntegrationResponseType = CreateDeleteIntegrationResponse;
export type UpdateIntegrationResponseType = UpdateAppSettingsResponse;

export interface UpdateIntegrationsResponseType {
    appId: string;
    integrations: Integration[];
}

export type UpdateIntegrationRequestType = UpdateIntegrationRequest;

export interface TryCreateIntegrationAction {
    type: AuthUpdatable;
    payload: CreateIntegrationRequestType;
}

// Access token was successfully requested and saved by Runway

export interface CreateIntegrationSuccessAction {
    type: typeof CREATE_INTEGRATION_REQUEST_SUCCESS;
    payload: CreateDeleteIntegrationResponseType;
}

// Update

export interface TryUpdateIntegrationAction {
    type: IntegrationUpdatable;
    payload: UpdateIntegrationRequestType;
}

export interface UpdateIntegrationSuccessAction {
    type: typeof UPDATE_INTEGRATION_REQUEST_SUCCESS;
    payload: UpdateIntegrationResponseType;
}

export interface UpdateIntegrationsSuccessAction {
    type: typeof UPDATE_INTEGRATIONS_REQUEST_SUCCESS;
    payload: UpdateIntegrationsResponseType;
}

// Delete

export interface DeleteIntegrationAction {
    type: IntegrationDeletable;
    payload: IntegrationRequest;
}

export interface DeleteIntegrationSuccessAction {
    type: typeof DELETE_INTEGRATION_REQUEST_SUCCESS;
    payload: CreateDeleteIntegrationResponseType;
}

// Actions

export const tryCreateIntegrationRequest: (
    type: AuthUpdatable,
    payload: CreateIntegrationRequestType,
) => TryCreateIntegrationAction = (type, payload) => {
    return {
        type,
        payload,
    };
};

export const createIntegrationSuccess: (
    response: CreateDeleteIntegrationResponseType,
) => CreateIntegrationSuccessAction = (response) => {
    return {
        type: CREATE_INTEGRATION_REQUEST_SUCCESS,
        payload: response,
    };
};

export const tryUpdateIntegrationRequest: (
    type: IntegrationUpdatable,
    payload: UpdateIntegrationRequestType,
) => TryUpdateIntegrationAction = (type, payload) => {
    return {
        type,
        payload,
    };
};

export const updateIntegrationSuccess: (response: UpdateIntegrationResponseType) => UpdateIntegrationSuccessAction = (
    response,
) => {
    return {
        type: UPDATE_INTEGRATION_REQUEST_SUCCESS,
        payload: response,
    };
};

export const updateIntegrationsSuccess: (
    response: UpdateIntegrationsResponseType,
) => UpdateIntegrationsSuccessAction = (response) => {
    return {
        type: UPDATE_INTEGRATIONS_REQUEST_SUCCESS,
        payload: response,
    };
};

export const tryDeleteIntegration: (
    type: IntegrationDeletable,
    payload: IntegrationRequest,
) => DeleteIntegrationAction = (type, payload) => {
    return {
        type,
        payload,
    };
};

export const deleteIntegrationSuccess: (
    response: CreateDeleteIntegrationResponseType,
) => DeleteIntegrationSuccessAction = (response) => {
    return {
        type: DELETE_INTEGRATION_REQUEST_SUCCESS,
        payload: response,
    };
};

export interface TryUpdateIntegrationAliasAction {
    type: typeof TRY_UPDATE_INTEGRATION_ALIAS;
    payload: UpdateIntegrationAliasRequest;
}

export const tryUpdateIntegrationAlias = (payload: UpdateIntegrationAliasRequest): TryUpdateIntegrationAliasAction => ({
    type: TRY_UPDATE_INTEGRATION_ALIAS,
    payload,
});
